import { useState, useContext, useEffect, useCallback } from 'react';
import UserTable from '../components/users/UserTable';
import {
  Box,
  Container,
  Modal,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  getCompanyDepartment,
  getAllUsers,
  addUserDepartment,
  editUserDepartment,
  deleteUser,
  deleteMultipleUser,
  getCompanyUsers,
  getCompanies,
} from '../apis/axios';
import AuthContext from '../AuthContext';
import Loading from '../components/common/Loading';

function Users() {
  const auth = useContext(AuthContext);
  const theme = useTheme();

  // === State ===
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [company, setCompany] = useState('');
  const [department, setDepartment] = useState('');
  const [userType, setUserType] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    username: '',
    password: '',
  });

  // === User Type Options ===
  const userTypeOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'superadmin', label: 'Superadmin Admin' },
    { value: 'installer', label: 'Installer' },
  ];

  const filteredUserTypeOptions =
    auth.userType === 'admin' && !isEditing
      ? userTypeOptions.filter((option) => option.value !== 'superadmin')
      : userTypeOptions;

  // === Fetch Users ===
  const fetchAllUsers = async () => {
    try {
      if (auth.userType === 'sa' && !auth.admin) {
        const response = await getAllUsers();
        setUsers(response.data);
      } else {
        const response = await getCompanyUsers({ company_id: auth.admin });
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // === Fetch Departments ===
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await getCompanyDepartment({company_id: auth.admin});
      if (response && Array.isArray(response.data)) {
        const formattedData = response.data.map((dept) => ({
          value: dept.id,
          label: dept.name,
        }));
        setDepartments(formattedData);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  }, [auth.admin]);

  // === Fetch Companies ===
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await getCompanies();
      if (response && Array.isArray(response.data)) {
        const formattedData = response.data.map((dept) => ({
          value: Number(dept.id),
          label: dept.name,
        }));
        setCompanyData(formattedData);
      } else {
        setCompanyData([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanyData([]);
    }
  }, []);

  useEffect(() => {
    fetchAllUsers();
  }, [auth.userType, auth.admin]);

  useEffect(() => {
    if (auth.admin) fetchDepartments();
    if (auth.userType === 'superadmin') fetchCompanies();
  }, [auth.admin, fetchDepartments, auth.userType, fetchCompanies]);

  // === Input Handlers ===
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const closeModal = () => {
    setOpenModal(false);
    setIsEditing(false);
    setFormData({ id: null, name: '', username: '', password: '' });
    setDepartment('');
    setUserType('');
    setCompany('');
    setShowPassword(false);
  };

  // === CRUD Handlers ===
  const handleAddUser = async () => {
    const newUser = {
      name: formData.name,
      username: formData.username,
      user_type: userType,
      department_id: department,
      password: formData.password,
      company_id: company,
    };
    try {
      await addUserDepartment(newUser);
      await fetchAllUsers();
      closeModal();
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  const handleUpdateUser = async () => {
    const ediUser = {
      id: formData.id,
      name: formData.name,
      username: formData.username,
      user_type: userType,
      department_id: department,
      password: formData.password,
      company_id: company,
    };
    try {
      await editUserDepartment(ediUser);
      await fetchAllUsers();
      closeModal();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleEditUser = (user) => {
    setFormData({
      id: user.id,
      name: user.name,
      username: user.username,
      password: user.password || '',
    });
    setDepartment(user.department ? String(user.department) : '');
    setUserType(user.user_type);
    setCompany(user.company_id);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser({ id: userId });
      await fetchAllUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // === Ripple Effect ===
  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add('export-button-ripple');
    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  };

  return (
    <Box display="flex" sx={{ justifyContent: 'center' }}>
      <Box flexGrow={1} sx={{ maxWidth: 'none' }}>
        <Container
          sx={{
            maxWidth: 'none !important',
            overflowX: 'hidden',
            px: 0,
          }}
        >
          <Box sx={{ width: '75.5vw', mx: 'auto' }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
                   flexDirection: { xs: 'column', md: 'row' }, // 👈 Mobile = column, Desktop = row
    gap: { xs: 2, md: 0 }, // optional spacing for mobile
              }}
            >
              <Typography variant="h4" fontWeight={700}>
                Users
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  handleRippleEffect(e);
                  setOpenModal(true);
                  setIsEditing(false);
                }}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 500,
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': { bgcolor: theme.palette.primary.dark },
                }}
              >
                Add User
              </Button>
            </Box>

            {/* User Table */}
            <UserTable
              data={users}
              setUsers={setUsers}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              onSelectionChange={setSelectedRows}
              userType={auth.userType}
            />
          </Box>

          {/* Add/Edit Modal */}
          <Modal
            open={openModal}
            onClose={closeModal}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              sx={{
                bgcolor: theme.palette.background.paper,
                p: 4,
                borderRadius: 3,
                width: { xs: '90vw', sm: '60vw', md: '45vw' },
                boxShadow: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {/* Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={700}>
                  {isEditing ? 'Edit User' : 'Add User'}
                </Typography>
                <IconButton onClick={closeModal}>
                  <CloseIcon color="error" />
                </IconButton>
              </Box>

              {/* Form Fields */}
              <Box display="flex" flexWrap="wrap" gap={2}>
                <TextField
                  label="Name*"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Username*"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Password*"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    ),
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel>User Type*</InputLabel>
                  <Select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    label="User Type*"
                  >
                    {filteredUserTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {auth.userType === 'admin' && (
                  <FormControl fullWidth>
                    <InputLabel>Department*</InputLabel>
                    <Select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      label="Department*"
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept.value} value={dept.value}>
                          {dept.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {auth.userType === 'superadmin' && (
                  <FormControl fullWidth>
                    <InputLabel>Company*</InputLabel>
                    <Select
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      label="Company*"
                    >
                      {companyData.map((comp) => (
                        <MenuItem key={comp.value} value={comp.value}>
                          {comp.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              {/* Action Buttons */}
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    borderRadius: '20px',
                    px: 4,
                    py: 1.2,
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                  disabled={!formData.name || !formData.username || !formData.password}
                  onClick={isEditing ? handleUpdateUser : handleAddUser}
                >
                  {isEditing ? 'Update User' : 'Add User'}
                </Button>
              </Box>
            </Box>
          </Modal>
        </Container>
      </Box>
    </Box>
  );
}

export default Users;
